import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import bg from "../assets/background.png";
import Footer from "../components/footer";
import Seo from "../components/Seo";
import { useSchedule, resolveEventStatus, mainEventDate } from "../data/useLocations";

const BRAND = "#d0142d";

const SR_ONLY = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};

const MONTHS = [
  "stycznia", "lutego", "marca", "kwietnia", "maja", "czerwca",
  "lipca", "sierpnia", "września", "października", "listopada", "grudnia",
];

/* Inline style mają wyższą specyficzność niż klasy - stąd !important. */
const EVENTS_CSS = `
@media (max-width: 700px) {
  .ev-card {
    flex-direction: column !important;
    border-left-width: 6px !important;
    border-radius: 18px !important;
  }
  .ev-card-img {
    flex: 0 0 auto !important;
    width: 100% !important;
    height: 170px !important;
    min-height: 0 !important;
  }
  .ev-card-body {
    padding: 14px 16px !important;
    gap: 6px !important;
  }
  .ev-card-title {
    font-size: 16px !important;
  }
}
`;

export function formatEventDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** Zakres dat wydarzenia: od najwcześniejszego "date" do najpóźniejszego "endDate || date". */
export function dateRangeLabel(dates) {
  if (!dates || dates.length === 0) return "Termin wkrótce";

  const starts = dates.map((d) => d?.date).filter(Boolean).sort();
  const ends = dates.map((d) => d?.endDate || d?.date).filter(Boolean).sort();

  const first = starts[0];
  const last = ends[ends.length - 1];

  if (!first) return "Termin wkrótce";
  if (first === last) return formatEventDate(first);

  const [y1, m1, d1] = first.split("-").map(Number);
  const [y2, m2, d2] = last.split("-").map(Number);

  if (y1 === y2 && m1 === m2) return `${d1}-${d2} ${MONTHS[m1 - 1]} ${y1}`;
  if (y1 === y2) return `${d1} ${MONTHS[m1 - 1]} - ${d2} ${MONTHS[m2 - 1]} ${y1}`;

  return `${formatEventDate(first)} - ${formatEventDate(last)}`;
}

/**
 * Pojedynczy termin z godzinami:
 *   wielodniowy   -> "24.10, 18:00 - 25.10, 8:00"
 *   jednodniowy   -> "24.10, 18:00 - 20:00"
 *   bez godzin    -> "24.10"
 */
export function dateTimeRangeLabel(d) {
  if (!d || !d.date) return "";

  const day = (iso) => {
    const [, m, dd] = iso.split("-").map(Number);
    return `${dd}.${String(m).padStart(2, "0")}`;
  };
  const time = (t) => (t ? String(t).replace(/^0/, "") : "");

  const endDate = d.endDate && d.endDate !== d.date ? d.endDate : null;
  const start = time(d.startTime);
  const end = time(d.endTime);

  const left = start ? `${day(d.date)}, ${start}` : day(d.date);

  if (endDate) {
    const right = end ? `${day(endDate)}, ${end}` : day(endDate);
    return `${left} - ${right}`;
  }

  if (end) return `${left} - ${end}`;
  return left;
}

/** Wiek: "7-12 lat", "od 7 lat", "do 12 lat" albo "" gdy brak danych. */
export function ageLabel(from, to) {
  if (from == null && to == null) return "";
  if (from != null && to != null) return `${from}-${to} lat`;
  if (from != null) return `od ${from} lat`;
  return `do ${to} lat`;
}

export default function EventsPage() {
  const { data, loading, error } = useSchedule();

  const events = data?.events || [];

  const { upcoming, past } = useMemo(() => {
    const up = [];
    const old = [];

    events.forEach((e) => {
      const status = resolveEventStatus(e);
      if (status === "past") old.push(e);
      else up.push({ ...e, _status: status });
    });

    // Nadchodzące: wyróżnione pierwsze, potem po najbliższej dacie.
    up.sort((a, b) => {
      if (a.featured !== b.featured) return a.featured ? -1 : 1;
      const da = mainEventDate(a)?.date || "9999";
      const db = mainEventDate(b)?.date || "9999";
      return da.localeCompare(db);
    });

    // Archiwalne: od najnowszych.
    old.sort((a, b) => {
      const da = mainEventDate(a)?.date || "";
      const db = mainEventDate(b)?.date || "";
      return db.localeCompare(da);
    });

    return { upcoming: up, past: old };
  }, [events]);

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        background: `#ededed url(${bg}) center / cover no-repeat`,
        paddingTop: "80px",
      }}
    >
      <style>{EVENTS_CSS}</style>

      <Seo
        title="Wydarzenia specjalne"
        description="Warsztaty, nocowanki, obozy i imprezy tematyczne 4STARS. Sprawdź, co szykujemy dla dzieci we Wrocławiu, Jastrzębiu-Zdroju i okolicach."
        path="/wydarzenia"
      />

      <h1 style={SR_ONLY}>
        Wydarzenia specjalne 4STARS - warsztaty, obozy i imprezy dla dzieci
      </h1>

      <section
        style={{
          width: "92%",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          margin: "20px auto",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "34px", fontWeight: 900, color: "rgba(0,0,0,0.92)" }}>
          Wydarzenia specjalne
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: "13px",
            fontWeight: 700,
            color: "rgba(0,0,0,0.62)",
            maxWidth: "1200px",
          }}
        >
          Warsztaty, nocowanki, obozy i imprezy tematyczne. Poza regularnymi zajęciami
          organizujemy wydarzenia, na które może przyjść każdy - także bez zapisu na stałe zajęcia.
        </p>
      </section>

      {loading ? (
        <section style={{ width: "92%", maxWidth: "1200px", display: "flex", flexDirection: "column", gap: "12px", paddingBottom: "60px" }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                display: "block",
                width: "100%",
                height: "140px",
                borderRadius: "22px",
                backgroundColor: "rgba(255,255,255,0.55)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            />
          ))}
        </section>
      ) : null}

      {!loading && error ? (
        <EmptyBox text="Nie udało się wczytać wydarzeń. Odśwież stronę lub spróbuj za chwilę." />
      ) : null}

      {!loading && !error && events.length === 0 ? (
        <EmptyBox text="Teraz nie mamy zaplanowanych wydarzeń. Zajrzyj tu za jakiś czas albo obserwuj nas na Instagramie." />
      ) : null}

      {upcoming.length > 0 ? (
        <>
          <h3
            style={{
              color: "rgb(30,30,30)",
              fontSize: "23px",
              fontWeight: "bold",
              textAlign: "center",
              marginTop: "20px",
            }}
          >
            Nadchodzące
          </h3>
          <section
            style={{
              width: "92%",
              maxWidth: "1200px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              marginTop: "10px",
            }}
          >
            {upcoming.map((e) => (
              <EventCard key={e.id} event={e} archived={false} />
            ))}
          </section>
        </>
      ) : null}

      {past.length > 0 ? (
        <>
          <hr style={{ border: `2px solid ${BRAND}`, width: "80%", borderRadius: "10px", marginTop: "30px" }} />
          <h3
            style={{
              color: "rgb(30,30,30)",
              fontSize: "23px",
              fontWeight: "bold",
              textAlign: "center",
              marginTop: "10px",
            }}
          >
            Co już za nami
          </h3>
          <section
            style={{
              width: "92%",
              maxWidth: "1200px",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "14px",
              marginTop: "10px",
              paddingBottom: "60px",
            }}
          >
            {past.map((e) => (
              <ArchiveCard key={e.id} event={e} />
            ))}
          </section>
        </>
      ) : null}

      {upcoming.length > 0 && past.length === 0 ? (
        <div style={{ paddingBottom: "60px" }} />
      ) : null}

      <Footer />
    </div>
  );
}

// ── KARTA NADCHODZĄCEGO WYDARZENIA ───────────────────────────────────────────

function EventCard({ event }) {
  const color = event.color || BRAND;
  const image = event.thumbUrl || event.coverUrl;
  const cancelled = event._status === "cancelled";

  return (
    <Link to={`/${event.slug}`} style={{ textDecoration: "none", width: "100%" }}>
      <div
        className="ev-card"
        style={{
          width: "100%",
          backgroundColor: "rgba(255,255,255,0.94)",
          border: "1px solid rgba(0,0,0,0.08)",
          borderLeft: `8px solid ${color}`,
          borderRadius: "22px",
          boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          overflow: "hidden",
          opacity: cancelled ? 0.65 : 1,
        }}
      >
        {image ? (
          <div
            className="ev-card-img"
            style={{
              flex: "0 0 220px",
              minHeight: "150px",
              backgroundImage: `url(${image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        ) : null}

        <div
          className="ev-card-body"
          style={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            padding: "18px 20px",
            minWidth: 0,
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
            <Badge color={color} filled>
              {dateRangeLabel(event.dates)}
            </Badge>

            {event.featured && !cancelled ? <Badge color={color}>Polecamy</Badge> : null}
            {cancelled ? <Badge color="#7a1111" filled>Odwołane</Badge> : null}
          </div>

          <h4
            className="ev-card-title"
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 900,
              color: "rgba(0,0,0,0.92)",
              lineHeight: 1.25,
            }}
          >
            {event.title}
          </h4>

          {event.subtitle ? (
            <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: color }}>
              {event.subtitle}
            </p>
          ) : null}

          <p
            style={{
              margin: 0,
              fontSize: "13px",
              fontWeight: 600,
              color: "rgba(0,0,0,0.62)",
              lineHeight: 1.55,
            }}
          >
            {event.lead}
          </p>

          <span
            style={{
              alignSelf: "flex-start",
              marginTop: "4px",
              fontSize: "12px",
              fontWeight: 900,
              color: "rgb(250,250,250)",
              backgroundColor: color,
              borderRadius: "999px",
              padding: "9px 16px",
            }}
          >
            Zobacz szczegóły
          </span>
        </div>
      </div>
    </Link>
  );
}

// ── KARTA ARCHIWALNA ─────────────────────────────────────────────────────────

function ArchiveCard({ event }) {
  const color = event.color || BRAND;
  const image = event.thumbUrl || event.coverUrl;

  return (
    <Link
      to={`/${event.slug}`}
      style={{ textDecoration: "none", flex: "1 1 280px", minWidth: "260px", maxWidth: "380px" }}
    >
      <div
        style={{
          backgroundColor: "rgba(255,255,255,0.94)",
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: "18px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {image ? (
          <div
            style={{
              width: "100%",
              height: "150px",
              backgroundImage: `url(${image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "grayscale(0.25)",
            }}
          />
        ) : null}

        <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "14px 16px" }}>
          <span style={{ fontSize: "11px", fontWeight: 900, color: "rgba(0,0,0,0.5)" }}>
            {dateRangeLabel(event.dates)}
          </span>
          <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 900, color: "rgba(0,0,0,0.92)" }}>
            {event.title}
          </h4>
          <span style={{ fontSize: "12px", fontWeight: 800, color: color, marginTop: "2px" }}>
            Zobacz relację
          </span>
        </div>
      </div>
    </Link>
  );
}

// ── POMOCNICZE ───────────────────────────────────────────────────────────────

function Badge({ children, color, filled }) {
  return (
    <span
      style={{
        fontSize: "11px",
        fontWeight: 900,
        borderRadius: "999px",
        padding: "4px 10px",
        color: filled ? "rgb(250,250,250)" : color,
        backgroundColor: filled ? color : `${color}1a`,
      }}
    >
      {children}
    </span>
  );
}

function EmptyBox({ text }) {
  return (
    <div
      style={{
        width: "92%",
        maxWidth: "1200px",
        backgroundColor: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: "18px",
        padding: "28px 16px",
        textAlign: "center",
        fontSize: "13px",
        fontWeight: 700,
        color: "rgba(0,0,0,0.6)",
        marginBottom: "60px",
      }}
    >
      {text}
    </div>
  );
}